import styled from '@emotion/styled'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Filter as Category } from 'app/components/Common/Filter'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { itemStringReplace } from 'app/utils/customFunctions'
import { useVocabularyData } from 'app/utils/vocabulary'
import { compact, uniq } from 'lodash'
import React, { memo, useState } from 'react'

import { Article, Props as ArticleProps } from './Article'

export interface Props {
  articles: ArticleProps[]
  languageCode: string
}

export const ArticlesList = memo(function ArticlesList({
  articles,
  languageCode,
}: Props) {
  if (!articles) {
    return null
  }

  const categories = compact(articles.map((item) => item.category))
  const [filteredCategories, setFilteredCategories] = useState('all')
  const [items, setItems] = useState(articles)

  return (
    <Container>
      {categories ? (
        <FadeInUp delay={1.1}>
          <Categories dial={5} row>
            <Category
              active={filteredCategories === 'all'}
              category="all"
              onClick={() => {
                setFilteredCategories('all')
                setItems(articles)
              }}
              title={useVocabularyData('all', languageCode)}
            />

            {uniq(compact(categories)).map((item, index) => (
              <Category
                active={filteredCategories === itemStringReplace(item)}
                category={itemStringReplace(item)}
                key={index}
                onClick={(e) => {
                  setFilteredCategories(e.target.getAttribute('data-category'))

                  setItems(
                    articles.filter((i) =>
                      i.category
                        ? itemStringReplace(i.category) ===
                          itemStringReplace(
                            e.target.getAttribute('data-category'),
                          )
                        : null,
                    ),
                  )
                }}
                title={item}
              />
            ))}
          </Categories>
        </FadeInUp>
      ) : null}

      <List row wrap>
        {uniq(items).map((item, index) => (
          <FadeInUp
            delay={index % 3 === 0 ? 0 : index % 3 === 1 ? 0.15 : 0.3}
            key={index}
          >
            <Article {...item} />
          </FadeInUp>
        ))}
      </List>
    </Container>
  )
})

const Container = styled.section`
  padding: 4.5rem 10.139vw 6rem;
  text-align: center;

  @media (max-width: 1199px) {
    padding: 2.5rem 0 5.25rem;
  }
`

const Categories = styled(FlexBox)`
  margin: 0 auto;

  @media (max-width: 1199px) {
    overflow-x: auto;
    padding: 0 1.5rem;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: 574px) {
    justify-content: flex-start;
  }
`

const List = styled(FlexBox)`
  margin-right: -5.486vw;

  > div {
    width: calc(33.333% - 5.486vw);
    margin: 4.5rem 5.486vw 0 0;
  }

  @media (max-width: 1199px) {
    margin: 3rem 0 0;
    padding: 0 3rem;
    position: relative;

    > div {
      width: 100%;
      margin: 3rem 0 0;

      &:first-of-type {
        margin-top: 0;
      }
    }
  }
`
